﻿const apiBase = () => (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) || '';

const authHeaders = (userId: string, orgId?: string): Record<string, string> => ({
  'Content-Type': 'application/json',
  'x-api-key': 'revisr',
  'x-user-id': userId,
  ...(orgId ? { 'x-org-id': orgId } : {}),
});

export type AdminFeedbackItem = {
  id: string;
  policy_id: string;
  org_id: string;
  user_id: string;
  rating: number;
  comment: string | null;
  quality_review: string | null;
  source: string;
  created_at: string;
  policyName: string | null;
  selectedDocument: string | null;
};

export type FeedbackStats = {
  averageRating: number;
  totalCount: number;
  ratingDistribution: Record<1 | 2 | 3 | 4 | 5, number>;
};

export const policyFeedbackExists = async (
  policyId: string,
  userId: string,
  orgId?: string,
): Promise<boolean> => {
  try {
    const res = await fetch(
      `${apiBase()}/api/revisr/policy-feedback/exists?policyId=${encodeURIComponent(policyId)}`,
      { headers: authHeaders(userId, orgId) },
    );
    if (!res.ok) return false;
    const data = await res.json();
    return !!data.exists;
  } catch {
    return false;
  }
};

/** Returns 'ok' | 'duplicate' | 'error'. Never throws — failure must not break the completed save. */
export const submitPolicyFeedback = async (
  input: { policyId: string; rating: number; comment?: string; qualityReview?: string },
  userId: string,
  orgId?: string,
): Promise<'ok' | 'duplicate' | 'error'> => {
  try {
    const res = await fetch(`${apiBase()}/api/revisr/policy-feedback`, {
      method: 'POST',
      headers: authHeaders(userId, orgId),
      body: JSON.stringify(input),
    });
    if (res.status === 409) return 'duplicate';
    return res.ok ? 'ok' : 'error';
  } catch {
    return 'error';
  }
};

export const fetchAdminFeedback = async (
  params: { orgId?: string; minRating?: number; limit?: number; offset?: number },
  userId: string,
  orgId?: string,
): Promise<{ items: AdminFeedbackItem[]; stats: FeedbackStats }> => {
  const q = new URLSearchParams();
  if (params.orgId) q.set('orgId', params.orgId);
  if (params.minRating !== undefined) q.set('minRating', String(params.minRating));
  if (params.limit !== undefined) q.set('limit', String(params.limit));
  if (params.offset !== undefined) q.set('offset', String(params.offset));
  const res = await fetch(`${apiBase()}/api/revisr/policy-feedback/admin?${q.toString()}`, {
    headers: authHeaders(userId, orgId),
  });
  if (!res.ok) throw new Error(`Failed to load feedback: ${res.status}`);
  return res.json();
};
